import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, BadgeCheck, BookOpen, ChevronDown, Clock3, KeyRound, Link2, ListChecks, MessageSquareText, ShieldCheck, Smartphone, UsersRound } from "lucide-react";
import telegramLogo from "@/assets/telegram.svg";
import HelpDemos from "./HelpDemos";
import ThemeToggle from "@/components/ThemeToggle";
import "./HelpPage.css";

const guideSteps = [
  {
    number: "01", title: "注册网站账号", icon: KeyRound,
    description: "填写昵称和 Telegram 用户名，向 @TGAutoCheckinbot 获取注册验证码，再设置登录密码。",
    hint: "已有账号可以直接登录；忘记密码时也可以选择验证码登录。",
  },
  {
    number: "02", title: "登录个人中心", icon: Smartphone,
    description: "使用 Telegram 用户名及密码登录，或选择验证码登录并通过机器人获取验证码。",
    hint: "登录后进入「账户概览」，可以查看连接和自动打卡状态。",
  },
  {
    number: "03", title: "连接 Telegram", icon: Link2,
    description: "打开「帐号连接」，输入带国家区号的手机号，获取 Telegram 官方验证码并完成连接。",
    hint: "如果账号开启了二步验证，还需要填写二步验证密码。",
  },
  {
    number: "04", title: "准备打卡内容", icon: MessageSquareText,
    description: "在「打卡配置 → 内容管理」添加文字、图片或视频；图片和视频也可以附带文字说明。",
    hint: "支持添加多条内容，系统会从已配置内容中选择发送。",
  },
  {
    number: "05", title: "选择目标群组", icon: UsersRound,
    description: "在「打卡配置 → 群组管理」选中需要打卡的群组，然后点击「保存打卡群组」。",
    hint: "请先确认您的 Telegram 账号已加入群组，且拥有发送消息的权限。",
  },
  {
    number: "06", title: "设定执行规则", icon: Clock3,
    description: "在「打卡配置 → 杂项设置」选择打卡时间段，并设置随机或固定打卡间隔。",
    hint: "建议采用合理的间隔，避免过于频繁地发送消息。",
  },
  {
    number: "07", title: "开启并查看记录", icon: BadgeCheck,
    description: "回到「账户概览」开启自动打卡，之后可在「打卡记录」查看各群组的执行状态。",
    hint: "需要临时执行时，可使用「立即打卡」；暂停时关闭自动打卡开关。",
  },
];

const faqs = [
  { question: "网站登录和 Telegram 连接有什么区别？", answer: "网站登录用于进入个人中心；Telegram 连接用于授权打卡服务使用您的账号发送消息。完成网站注册和登录后，仍需在「帐号连接」中单独连接 Telegram。" },
  { question: "收不到验证码或连接失败怎么办？", answer: "先检查手机号是否包含正确的国家区号，并在 Telegram 客户端查看官方验证码。若提示请求过于频繁，请等待页面显示的时间后重试；开启二步验证的账号还需输入对应密码。" },
  { question: "为什么有群组没有打卡记录？", answer: "请确认已在「群组管理」选中并保存该群组、账号在群组中有发言权限，且自动打卡处于开启状态。执行结果和连续失败次数可在「打卡记录」查看。" },
  { question: "如何调整打卡频率？", answer: "进入「打卡配置 → 杂项设置」，选择随机间隔或固定间隔并保存。您也可以启用时间段，让打卡只在指定时间内执行。" },
];

export default function HelpPage() {
  const { token } = useAuth();

  return (
    <div className="help-page">
      <header className="help-header">
        <Link to={token ? "/dashboard" : "/"} className="help-brand"><span><img src={telegramLogo} alt="" /></span><strong>TG Checkin</strong></Link>
        <nav aria-label="页面导航"><ThemeToggle /><Link to={token ? "/dashboard" : "/login"}>{token ? "个人中心" : "登录"}</Link><Link className="help-header-action" to={token ? "/dashboard" : "/register"}>{token ? "返回个人中心" : "注册账号"}<ArrowRight size={15} /></Link></nav>
      </header>

      <main className="help-main">
        <section className="help-hero"><div className="help-hero-content"><h1>使用指南</h1><p>按顺序完成注册、账号连接和打卡配置。下方有可点击的操作演示。</p></div></section>

        <div className="help-layout">
          <aside className="help-toc" aria-label="指南目录"><span>本页目录</span><a href="#quick-start"><ListChecks size={16} /> 设置步骤</a><a href="#walkthrough"><Smartphone size={16} /> 操作演示</a><a href="#faq"><BookOpen size={16} /> 常见问题</a></aside>
          <div className="help-content">
            <section id="quick-start" className="help-section"><div className="help-section-heading"><h2>设置步骤</h2></div><div className="help-steps">{guideSteps.map(({ number, title, icon: Icon, description, hint }) => <article className="help-step" key={number}><div className="help-step-marker"><span>{number}</span></div><div className="help-step-body"><div className="help-step-heading"><span className="help-tile-icon is-blue"><Icon size={18} /></span><h3>{title}</h3></div><p>{description}</p><small>{hint}</small></div></article>)}</div><div className="help-note"><ShieldCheck size={18} /><p>真实打卡会向群组发送消息。开启前请检查内容、群组权限和发送间隔。</p></div></section>

            <section id="walkthrough" className="help-section"><div className="help-section-heading"><h2>操作演示</h2></div><HelpDemos signedIn={!!token} /></section>

            <section id="faq" className="help-section"><div className="help-section-heading"><h2>常见问题</h2></div><div className="help-faq">{faqs.map(({ question, answer }) => <details key={question}><summary><span>{question}</span><ChevronDown size={18} /></summary><p>{answer}</p></details>)}</div></section>

          </div>
        </div>
      </main>
    </div>
  );
}
